import type { DashboardOverview } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import { formatNumber } from "@/utils/tools";
import { Button, Card, Icon, Skeleton, Typography } from "@st-marketplace/ui";
import { Link } from "@tanstack/react-router";
import type { FC } from "react";

export const DashboardSendersHealth: FC<{
	data: DashboardOverview;
	loading?: boolean;
}> = ({ data, loading }) => {
	const { t } = useTranslation();
	const broken = data.num_of_broken_senders ?? 0;
	const available = data.num_of_available_senders ?? 0;
	const total = broken + available;
	const operationalPercent = total > 0 ? (available / total) * 100 : 0;
	const brokenPercent = total > 0 ? (broken / total) * 100 : 0;

	return (
		<Card
			className="sender-health flex min-w-0 flex-col gap-5 p-5 sm:p-6"
			aria-busy={loading}
		>
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<Typography variant="title-sm" as="h2">
						{t("home.senderAccount")}
					</Typography>
					{loading ? (
						<Skeleton className="mt-2 h-4 w-32" />
					) : (
						<Typography
							variant="body-sm"
							as="p"
							className="mt-1 text-secondary-icon"
						>
							{total > 0
								? t("home.totalSenders", { count: total })
								: t("home.noSenders")}
						</Typography>
					)}
				</div>
				<Link to="/senders">
					<Button type="button" variant="text" color="primary" size="sm">
						{t("home.viewDetailsButton")}
					</Button>
				</Link>
			</div>

			{loading ? (
				<Skeleton className="h-36 w-full rounded-xl" />
			) : (
				<div className="sender-health-panel">
					<div className="flex flex-wrap items-baseline gap-2">
						<Typography
							variant="title-lg"
							as="p"
							className="text-3xl font-semibold tabular-nums"
						>
							{total > 0 ? formatNumber(operationalPercent, "percent") : "—"}
						</Typography>
						<Typography variant="body-sm" className="text-secondary-text-100">
							{t("home.successful")}
						</Typography>
					</div>

					<div className="sender-health-bar" aria-hidden="true">
						<div
							className="bg-success-text"
							style={{ width: `${operationalPercent}%` }}
						/>
						<div
							className="bg-danger-text"
							style={{ width: `${brokenPercent}%` }}
						/>
					</div>

					<div className="sender-health-counts">
						<div>
							<div className="flex items-center gap-2 text-success-text">
								<Icon name="checkbox-circle-line" size="sm" />
								<Typography variant="body-sm">
									{t("home.successful")}
								</Typography>
							</div>
							<Typography
								variant="title-md"
								as="p"
								className="mt-1 tabular-nums"
							>
								{formatNumber(available, "count")}
							</Typography>
						</div>
						<div className="text-end">
							<div className="flex items-center justify-end gap-2 text-danger-text">
								<Icon name="error-warning-line" size="sm" />
								<Typography variant="body-sm">
									{t("home.unsuccessful")}
								</Typography>
							</div>
							<div className="mt-1 flex flex-wrap items-baseline justify-end gap-2">
								<Typography variant="title-md" as="p" className="tabular-nums">
									{formatNumber(broken, "count")}
								</Typography>
								{total > 0 && (
									<Typography variant="caption" className="text-danger-text">
										{formatNumber(brokenPercent, "percent")}
									</Typography>
								)}
							</div>
						</div>
					</div>
				</div>
			)}
		</Card>
	);
};
